import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
    Box,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import EventNoteIcon from "@mui/icons-material/EventNote";

import { TripItinerary } from "./TripItinerary";

import { useAppDispatch, useAppSelector } from "src/Redux/store";
import {
    selectTripPlanner,
    saveTripAsQuote,
    convertTripToCart,
} from "src/Redux/Slices/tripPlanner";
import { selectAuth } from "src/Redux/Slices/auth";
import { computeTripTotal, hasUnavailableItems } from "src/Utilities/tripPlannerHelpers";
import { sessionHelper } from "@utilities";
import { LoadingStage } from "src/Interfaces/shared";
import { PATH_CART, PATH_LOGIN } from "@routes";

const Bar = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    background: #fafafa;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-top: 12px;
    flex-wrap: wrap;
`;

const Stat = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 80px;
`;

const StatLabel = styled.div`
    font-size: 11px;
    color: #757575;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const StatValue = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
`;

const TotalValue = styled(StatValue)`
    color: #d32f2f;
    font-size: 20px;
`;

export const TripSummary = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { current, saveStage, convertStage, lastError } = useAppSelector(selectTripPlanner);
    const { jwt } = useAppSelector(selectAuth);
    const isLoggedIn = jwt !== null && jwt !== undefined;

    const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" } | null>(null);
    const [itineraryOpen, setItineraryOpen] = useState(false);

    const itemCount = current.items.length;
    const total = useMemo(() => computeTripTotal(current), [current]);
    const unavailable = useMemo(() => hasUnavailableItems(current), [current]);
    const checking = current.items.some((i) => i.availabilityStatus === "checking");

    const saving = saveStage === LoadingStage.IS_LOADING;
    const converting = convertStage === LoadingStage.IS_LOADING;

    const handleSave = async () => {
        // Saving a trip persists it as a quote, which the backend gates on
        // an authenticated session. Match the existing pattern used by the
        // product page: bounce the user to /login with a returnPath so they
        // come straight back here after signing in.
        if (!isLoggedIn) {
            setToast({
                msg: "Please log in to save your trip.",
                severity: "error",
            });
            const returnPath = encodeURIComponent(
                window.location.pathname + window.location.search,
            );
            setTimeout(() => navigate(`${PATH_LOGIN}?returnPath=${returnPath}`), 600);
            return;
        }
        const result = await dispatch(saveTripAsQuote());
        if (saveTripAsQuote.fulfilled.match(result)) {
            setToast({ msg: `Trip saved (id: ${result.payload})`, severity: "success" });
        } else {
            setToast({
                msg: (result.payload as string) || lastError || "Failed to save trip",
                severity: "error",
            });
        }
    };

    const handleAddToCart = async () => {
        const sessionId = sessionHelper.hasSession() ? sessionHelper.getSessionId() : undefined;
        const result = await dispatch(convertTripToCart({ sessionId }));
        if (convertTripToCart.fulfilled.match(result)) {
            setToast({ msg: "Items added to cart", severity: "success" });
            const target = sessionId ? `${PATH_CART}?sessionId=${sessionId}` : PATH_CART;
            // Tiny delay so the user sees the success snackbar before nav.
            setTimeout(() => navigate(target), 400);
        } else {
            setToast({
                msg: (result.payload as string) || lastError || "Failed to add to cart",
                severity: "error",
            });
        }
    };

    const cartTooltip = unavailable
        ? "Remove or move unavailable items first"
        : checking
        ? "Waiting for availability checks to finish"
        : itemCount === 0
        ? "Add at least one item to your trip"
        : "";

    const saveTooltip =
        itemCount === 0
            ? "Add at least one item to your trip"
            : !isLoggedIn
            ? "Log in to save your trip"
            : "";

    return (
        <>
            <Bar>
                <Stat>
                    <StatLabel>Days</StatLabel>
                    <StatValue>{current.durationDays}</StatValue>
                </Stat>
                <Stat>
                    <StatLabel>Items</StatLabel>
                    <StatValue>{itemCount}</StatValue>
                </Stat>
                <Stat>
                    <StatLabel>Total</StatLabel>
                    <TotalValue>${total.toFixed(0)}</TotalValue>
                </Stat>
                <Box sx={{ flex: 1 }} />
                <Tooltip
                    title={itemCount === 0 ? "Add at least one item to your trip" : ""}
                >
                    <span>
                        <Button
                            variant="outlined"
                            startIcon={<EventNoteIcon />}
                            onClick={() => setItineraryOpen(true)}
                            disabled={itemCount === 0}
                        >
                            View Itinerary
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={saveTooltip}>
                    <span>
                        <Button
                            variant="outlined"
                            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={saving || itemCount === 0}
                        >
                            {saving ? "Saving..." : "Save Trip"}
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={cartTooltip}>
                    <span>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={
                                converting ? <CircularProgress size={16} /> : <ShoppingCartIcon />
                            }
                            onClick={handleAddToCart}
                            disabled={
                                converting || itemCount === 0 || unavailable || checking
                            }
                        >
                            {converting ? "Adding..." : "Add to Cart"}
                        </Button>
                    </span>
                </Tooltip>
            </Bar>
            <Snackbar
                open={!!toast}
                autoHideDuration={4000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                {toast ? (
                    <Alert
                        onClose={() => setToast(null)}
                        severity={toast.severity}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {toast.msg}
                    </Alert>
                ) : undefined}
            </Snackbar>
            <TripItinerary
                open={itineraryOpen}
                trip={current}
                onClose={() => setItineraryOpen(false)}
            />
        </>
    );
};

export default TripSummary;
